/**
 * Per-industry secondary image + service-area keyword block for
 * `*-marketing-colorado` pages. Renders under the hero pricing as a
 * 2-column section that injects industry-specific keywords for SEO.
 */

import hvacSecondary from "@/assets/industries/hvac-secondary.jpg";
import plumbingSecondary from "@/assets/industries/plumbing-secondary.jpg";
import roofingSecondary from "@/assets/industries/roofing-secondary.jpg";
import electricalSecondary from "@/assets/industries/electrical-secondary.jpg";
import landscapingSecondary from "@/assets/industries/landscaping-secondary.jpg";
import paintingSecondary from "@/assets/industries/painting-secondary.jpg";
import concreteSecondary from "@/assets/industries/concrete-secondary.jpg";
import cleaningSecondary from "@/assets/industries/cleaning-secondary.jpg";
import dentalSecondary from "@/assets/industries/dental-secondary.jpg";
import medicalSecondary from "@/assets/industries/medical-secondary.jpg";
import chiropracticSecondary from "@/assets/industries/chiropractic-secondary.jpg";
import autoDealerSecondary from "@/assets/industries/auto-dealer-secondary.jpg";
import autoRepairSecondary from "@/assets/industries/auto-repair-secondary.jpg";
import garageDoorSecondary from "@/assets/industries/garage-door-secondary.jpg";
import movingSecondary from "@/assets/industries/moving-secondary.jpg";
import pestControlSecondary from "@/assets/industries/pest-control-secondary.jpg";
import treeServiceSecondary from "@/assets/industries/tree-service-secondary.jpg";
import dogTrainerSecondary from "@/assets/industries/dog-trainer-secondary.jpg";
import emailSecondary from "@/assets/industries/email-secondary.jpg";

export type IndustrySecondaryConfig = {
  image: string;
  alt: string;
  /** 6–10 service or sub-service keywords. */
  services: string[];
};

const SPRINGS_AREAS = [
  "Colorado Springs",
  "Denver",
  "Castle Rock",
  "Monument",
  "Fort Collins",
  "Pueblo",
  "Falcon",
  "Boulder",
];

export const INDUSTRY_SECONDARY: Record<string, IndustrySecondaryConfig> = {
  "hvac-marketing-colorado": {
    image: hvacSecondary,
    alt: "Residential AC condenser and furnace serviced by a Colorado HVAC technician",
    services: [
      "AC repair & installation",
      "Furnace repair & replacement",
      "Heat pump installation",
      "Ductless mini-split systems",
      "Ductwork & indoor air quality",
      "Emergency 24/7 HVAC service",
      "Commercial rooftop HVAC",
      "Annual maintenance plans",
    ],
  },
  "plumbing-marketing-colorado": {
    image: plumbingSecondary,
    alt: "Colorado plumber repairing a copper supply line under a kitchen sink",
    services: [
      "Drain cleaning & hydro jetting",
      "Water heater repair & install",
      "Tankless water heaters",
      "Sewer line repair & replacement",
      "Slab leak detection",
      "Repiping & remodels",
      "24/7 emergency plumbing",
      "Backflow testing & prevention",
    ],
  },
  "roofing-marketing-colorado": {
    image: roofingSecondary,
    alt: "Colorado roofing crew installing impact-resistant shingles with mountain views",
    services: [
      "Asphalt shingle roofing",
      "Metal roof installation",
      "Hail & storm damage repair",
      "Insurance claim assistance",
      "Roof inspections",
      "Gutter replacement",
      "Flat & TPO commercial roofing",
      "Class 4 impact-resistant shingles",
    ],
  },
  "electrical-contractor-marketing-colorado": {
    image: electricalSecondary,
    alt: "Colorado electrician installing a new breaker in a residential electrical panel",
    services: [
      "Panel upgrades & replacements",
      "EV charger installation",
      "Whole-home generator install",
      "Recessed & LED lighting",
      "Knob & tube rewiring",
      "Hot tub & spa wiring",
      "Commercial electrical service",
      "24/7 emergency electrician",
    ],
  },
  "landscaping-marketing-colorado": {
    image: landscapingSecondary,
    alt: "Colorado landscaper installing turf and sprinkler heads at a Front Range home",
    services: [
      "Landscape design & install",
      "Sod & xeriscaping",
      "Sprinkler & drip irrigation",
      "Retaining walls & flagstone patios",
      "Tree & shrub planting",
      "Weekly lawn maintenance",
      "Snow removal services",
      "Outdoor lighting installation",
    ],
  },
  "painting-marketing-colorado": {
    image: paintingSecondary,
    alt: "Colorado house painter rolling exterior paint on craftsman siding",
    services: [
      "Interior house painting",
      "Exterior repaints",
      "Cabinet refinishing",
      "Deck staining & sealing",
      "Stucco painting",
      "Commercial painting",
      "Color consultation",
      "Drywall repair & texture matching",
    ],
  },
  "concrete-marketing-colorado": {
    image: concreteSecondary,
    alt: "Colorado concrete contractor finishing a freshly poured residential driveway",
    services: [
      "Driveways & approaches",
      "Stamped & decorative concrete",
      "Patios & walkways",
      "Concrete repair & resurfacing",
      "Foundations & footings",
      "Retaining walls",
      "Garage floor coatings",
      "Commercial flatwork",
    ],
  },
  "cleaning-marketing-colorado": {
    image: cleaningSecondary,
    alt: "Professional cleaner wiping a granite countertop in a bright Colorado home",
    services: [
      "Recurring residential cleaning",
      "Deep cleaning",
      "Move-in / move-out cleans",
      "Airbnb & short-term turnovers",
      "Post-construction cleaning",
      "Office & commercial cleaning",
      "Window cleaning",
      "Carpet & upholstery cleaning",
    ],
  },
  "dental-marketing-colorado": {
    image: dentalSecondary,
    alt: "Colorado dentist examining a smiling patient in a modern operatory",
    services: [
      "General & family dentistry",
      "Cosmetic dentistry & veneers",
      "Dental implants",
      "Invisalign & orthodontics",
      "Emergency dental care",
      "Pediatric dentistry",
      "Teeth whitening",
      "Same-day crowns",
    ],
  },
  "medical-marketing-colorado": {
    image: medicalSecondary,
    alt: "Colorado physician consulting with a patient in a modern clinic exam room",
    services: [
      "Primary care",
      "Urgent care visits",
      "Telehealth appointments",
      "Pediatric care",
      "Women's health",
      "Concierge medicine",
      "Med spa & aesthetics",
      "Physical therapy",
    ],
  },
  "chiropractic-marketing-colorado": {
    image: chiropracticSecondary,
    alt: "Chiropractor adjusting a patient on a treatment table in a Colorado clinic",
    services: [
      "Spinal adjustments",
      "Auto accident & whiplash care",
      "Sports injury rehab",
      "Pediatric chiropractic",
      "Pregnancy chiropractic",
      "Massage therapy",
      "Dry needling",
      "Decompression therapy",
    ],
  },
  "auto-dealer-marketing-colorado": {
    image: autoDealerSecondary,
    alt: "Colorado car dealership salesperson handing keys to a happy customer",
    services: [
      "New car sales",
      "Used & certified pre-owned",
      "Trade-in appraisals",
      "Financing & leasing",
      "Service & parts department",
      "Fleet & commercial sales",
      "EV inventory",
      "Online buying experience",
    ],
  },
  "auto-repair-marketing-colorado": {
    image: autoRepairSecondary,
    alt: "Colorado auto mechanic using a diagnostic scanner under the hood",
    services: [
      "Brake repair & rotors",
      "Oil changes",
      "Check-engine diagnostics",
      "Transmission service",
      "Suspension & alignments",
      "AC repair & recharge",
      "Tire sales & install",
      "Fleet maintenance",
    ],
  },
  "garage-door-marketing-colorado": {
    image: garageDoorSecondary,
    alt: "Colorado garage door technicians replacing a residential torsion spring",
    services: [
      "Spring replacement",
      "Cable & roller repair",
      "Garage door opener install",
      "New door installation",
      "Off-track repair",
      "Smart opener & Wi-Fi setup",
      "Commercial overhead doors",
      "Same-day emergency repair",
    ],
  },
  "moving-company-marketing-colorado": {
    image: movingSecondary,
    alt: "Colorado movers loading a sofa into a moving truck at a residential home",
    services: [
      "Local residential moves",
      "Long-distance moves",
      "Apartment & condo moves",
      "Packing & unpacking",
      "Loading & labor only",
      "Office & commercial relocation",
      "Piano & specialty moving",
      "Short & long-term storage",
    ],
  },
  "pest-control-marketing-colorado": {
    image: pestControlSecondary,
    alt: "Colorado pest control technician treating a residential kitchen with a sprayer",
    services: [
      "Mice & rodent removal",
      "Wasp & hornet nest removal",
      "Spider treatments",
      "Bed bug heat treatments",
      "Mosquito yard treatments",
      "Ant & roach extermination",
      "Quarterly residential plans",
      "Commercial & restaurant accounts",
    ],
  },
  "tree-service-marketing-colorado": {
    image: treeServiceSecondary,
    alt: "Colorado arborist climbing a large tree with chainsaw and harness",
    services: [
      "Tree removal",
      "Tree trimming & pruning",
      "Emergency storm cleanup",
      "Stump grinding & removal",
      "Cabling & bracing",
      "Tree health assessments",
      "Pine beetle treatments",
      "Commercial tree care",
    ],
  },
  "dog-trainer-marketing-colorado": {
    image: dogTrainerSecondary,
    alt: "Colorado dog trainer working with a golden retriever in a mountain park",
    services: [
      "Puppy training",
      "Basic obedience",
      "Board-and-train programs",
      "Service dog training",
      "Behavior modification",
      "Off-leash training",
      "Group classes",
      "In-home private training",
    ],
  },
  "auto-detailing-marketing-colorado": {
    image: autoRepairSecondary,
    alt: "Colorado auto detailer working on a vehicle in a clean shop",
    services: [
      "Interior detailing",
      "Exterior wash & wax",
      "Paint correction",
      "Ceramic coatings",
      "Headlight restoration",
      "Mobile detailing service",
      "Fleet detailing",
      "Pre-sale detail packages",
    ],
  },
  "email-marketing-colorado": {
    image: emailSecondary,
    alt: "Colorado marketer reviewing email campaign analytics on a laptop",
    services: [
      "Email automation flows",
      "Newsletter design & writing",
      "Klaviyo & Mailchimp setup",
      "List growth & lead magnets",
      "Abandoned-cart sequences",
      "Re-engagement campaigns",
      "SMS marketing integration",
      "A/B testing & reporting",
    ],
  },
};

export function getIndustrySecondaryConfig(
  slug: string,
): IndustrySecondaryConfig | null {
  return INDUSTRY_SECONDARY[slug] ?? null;
}

export { SPRINGS_AREAS };
